import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { llmReasoningApi } from '../api/client'
import { useExpandablePanel } from '../hooks/useExpandablePanel'
import { ExpandToggle } from './ExpandToggle'
import './LLMPrecedentList.css'

export function LLMPrecedentList() {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['llm-reasoning-precedents'],
    queryFn: () => llmReasoningApi.precedents(true, 50),
    refetchInterval: 60000,
  })
  const [pendingId, setPendingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const invalidate = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      llmReasoningApi.invalidatePrecedent(id, reason),
    onSuccess: () => {
      setError(null)
      setPendingId(null)
      qc.invalidateQueries({ queryKey: ['llm-reasoning-precedents'] })
    },
    onError: (e: Error) => {
      setError(e.message || 'Invalidate failed')
      setPendingId(null)
    },
  })

  function handleInvalidate(id: number) {
    const reason = window.prompt(
      `Reason for invalidating precedent #${id}? (1-500 chars)`,
    )
    if (!reason) return
    setPendingId(id)
    invalidate.mutate({ id, reason: reason.slice(0, 500) })
  }

  const { expanded, toggle, scrollClassName } = useExpandablePanel('llm-precedents')

  return (
    <div className="card llm-precedent-list">
      <div className="panel-head">
        <h3>LLM precedents · active</h3>
        <ExpandToggle expanded={expanded} onToggle={toggle} />
      </div>
      {q.isLoading && <p className="llm-muted">Loading…</p>}
      {q.isError && <p className="llm-muted">Failed to load precedents.</p>}
      {q.data && q.data.items.length === 0 && (
        <p className="llm-muted">No active precedents.</p>
      )}
      {q.data && q.data.items.length > 0 && (
        <div className={scrollClassName}><table className="llm-precedent-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Goal</th>
              <th>Dir</th>
              <th>Bucket</th>
              <th>Outcome</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{p.goal_type}</td>
                <td>{p.direction}</td>
                <td>{p.magnitude_bucket}</td>
                <td>
                  <span className={`llm-precedent-outcome llm-precedent-${p.outcome ?? 'pending'}`}>
                    {p.outcome ?? 'pending'}
                  </span>
                </td>
                <td className="llm-precedent-when">
                  {p.created_at
                    ? p.created_at.replace('T', ' ').slice(0, 19) + ' UTC'
                    : '—'}
                </td>
                <td>
                  <button
                    className="llm-precedent-invalidate"
                    onClick={() => handleInvalidate(p.id)}
                    disabled={pendingId === p.id}
                  >
                    {pendingId === p.id ? '…' : 'Invalidate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {error && <p className="llm-precedent-error">{error}</p>}
    </div>
  )
}
